/**
 * ReMixCharts.tsx
 *
 * A versatile charting component that uses ECharts to render various chart types.
 * This component supports line charts, bar charts, and area charts with customizable
 * styling, dual Y-axes, interactive legends, data zooming, and optimized rendering
 * for large datasets.
 */

import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import ReactECharts from "echarts-for-react";
import { SecondaryColor } from "../../common";
import {
  BAR_RADIUS,
  BrushStart,
  ChartArguements,
  ChartType,
  LegendKey,
} from "../../models/chart_model";
import { COST_UNIT } from "../../Units";
import { getTimeRange } from "../../pages/Dashboard/Exchange3/Chart";
import { useDispatch } from "react-redux";
import { setLoadingFilterName } from "../../store/state/FilterLoadingState";
import { getColor } from "../../utility/Colors";
import { monthNames } from "../../pages/Dashboard/PriceForcating/IexMonthly";

const COLORS = [
  "#FF7F50",
  "#B8860B",
  "#7CB5EC",
  "#F1935C",
  "#34656D",
  "#333333",
];

/**
 * Lightens a hex color by the specified percentage
 */
export function lightenColor(color: string, percent: number): string {
  const num = parseInt(color.replace("#", ""), 16),
    amt = Math.round(2.55 * percent),
    R = (num >> 16) + amt,
    G = ((num >> 8) & 0x00ff) + amt,
    B = (num & 0x0000ff) + amt;

  return `#${(
    0x1000000 +
    (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
    (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
    (B < 255 ? (B < 1 ? 0 : B) : 255)
  )
    .toString(16)
    .slice(1)}`;
}

/**
 * Determines the appropriate color for a legend based on selection state
 */
const getLegendColor = (
  legend: LegendKey,
  index: number,
  selectedLegends: LegendKey[],
  isLegend?: boolean
): string => {
  const isSelected =
    selectedLegends.some((item) => item.dataKey === legend.dataKey) ||
    selectedLegends.length === 0;

  return isSelected
    ? (isLegend ? legend.legendColor : legend.stroke) ??
        legend.stroke ??
        COLORS[index % COLORS.length]
    : "#E0E0E0";
};

/**
 * Component for rendering a legend item with appropriate styling
 */
export const LegendComponent = (
  legend: LegendKey,
  index: number,
  selectedLegends: LegendKey[],
  handleLegendClick: (legend: LegendKey) => void,
  circleOnly?: boolean,
  fontSize = 12,
  colorMap?: Record<string, string>,
  isSmallLegend = false
) => {
  const isSelected =
    selectedLegends.some((item) => item.dataKey === legend.dataKey) ||
    selectedLegends.length === 0;

  const color = isSelected
    ? legend.legendColor ||
      legend.stroke ||
      colorMap?.[legend.name || ""] ||
      getLegendColor(legend, index, selectedLegends, true)
    : "#E0E0E0";

  return (
    <div
      key={legend.dataKey}
      className={"line-legend"}
      style={{
        fontSize: fontSize.toString() + `px`,
      }}
      onClick={() => handleLegendClick(legend)}
    >
      <div style={{ color }}>
        <div
          className={
            legend.type === ChartType.Bar || circleOnly
              ? "dot"
              : legend.isDash
              ? "dash"
              : "line"
          }
          style={{
            marginRight: "6px",
            backgroundColor: color,
            fontSize: isSmallLegend ? "10px!important" : undefined,
            width:
              isSmallLegend && legend.type !== ChartType.Bar
                ? "10px"
                : undefined,
            borderColor: color,
          }}
        ></div>
        {legend.name}
      </div>
    </div>
  );
};

/**
 * Main chart component that renders various chart types using ECharts
 */
export function ReMixChart({
  data,
  legends,
  onLegendSelect,
  syncid,
  unit = "",
  overflowLegend = false,
  xDataKey,
  xTick,
  xLabel,
  yAxisLabel,
  legendFontSize,
  secondYAxisLabel,
  yAxisWidth,
  legendBreakIndex,
  hidePrice,
  circleLegend = false,
  xAxisLabelPosition = "insideBottom",
  xAxisLabelOffset,
  onlyTitle = false,
  disableLegendClick = false,
  secondXDataKey,
  showBrush = false,
  brushIndex,
  labelFontSize,
  rendexSecondXTick,
  brushHeight = 20,
  brushStart = BrushStart.End,
  xaxisHeight = 50,
  secondYAxisWidth,
  secondXAxisHeight = 12,
  barCategoryGap = 2.5,
  xAxisTickMargin = 8,
  isTimeSlot,
  xAxisAngle,
  onBarClick,
  fontSize = 12,
  secondXAxisLabel,
  customTooltip,
  hiddenLegends = [],
  hiddenStats = [],
  enableMouseWheelZoom = true,
  chartContainerHeight = "",
  nameGap,
  maxYsize,
  showToolbox = true,
  yMaxValue,
  tooltipFilter,
  legendMaxHeight,
  isPriceForecast = false,
  defaultSelectedLegends = [],
  top = 30,
  bottom = 20,
  left,
  yAxisDomain,
  secondYAxisDomain,
  isSmallLegend = false,
  customLegendOrder,
}: ChartArguements) {
  const [selectedLegends, setSelectedLegends] = useState<LegendKey[]>(
    defaultSelectedLegends.length > 0 ? defaultSelectedLegends : legends
  );

  useEffect(() => {
    setSelectedLegends(
      defaultSelectedLegends.length > 0 ? defaultSelectedLegends : legends
    );
  }, [JSON.stringify(defaultSelectedLegends), JSON.stringify(legends)]);

  const dispatch = useDispatch();
  const chartRef = useRef<ReactECharts>(null);

  const debouncedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data;
  }, [data]);

  const handleLegendClick = useCallback(
    (legend: LegendKey) => {
      if (disableLegendClick) return;

      setSelectedLegends((prevSelectedLegends) => {
        const allSelected =
          prevSelectedLegends.length === legends.length ||
          prevSelectedLegends.length === 0;

        let newSelectedLegends;
        if (allSelected) {
          newSelectedLegends = [legend];
        } else {
          const exists = prevSelectedLegends.some(
            (item) => item.dataKey === legend.dataKey
          );
          newSelectedLegends = exists
            ? prevSelectedLegends.filter(
                (item) => item.dataKey !== legend.dataKey
              )
            : [...prevSelectedLegends, legend];
        }

        if (onLegendSelect) {
          onLegendSelect(newSelectedLegends);
        }

        return newSelectedLegends;
      });
    },
    [disableLegendClick, onLegendSelect, legends]
  );

  const filteredLegends = useMemo(
    () =>
      legends
        .filter((e) => e.dataKey !== xDataKey)
        .sort((a, b) => {
          const nameA = (a.name || "").toLowerCase();
          const nameB = (b.name || "").toLowerCase();
          return nameA.localeCompare(nameB);
        }),
    [legends, xDataKey]
  );

  const colorMap = useMemo(() => {
    const map: Record<string, string> = {};
    filteredLegends.forEach((legend, index) => {
      if (legend.name) {
        map[legend.name] = getColor(index, legend.name);
      }
    });
    return map;
  }, [filteredLegends]);

  const orderedSelectedLegends = useMemo(() => {
    if (selectedLegends.length === 0) return filteredLegends;
    return legends.filter((legend) =>
      selectedLegends.some((selected) => selected.dataKey === legend.dataKey)
    );
  }, [selectedLegends, filteredLegends, legends]);

  const xAxisData = useMemo(() => {
    return debouncedData?.map((item) => item[xDataKey]) || [];
  }, [debouncedData, xDataKey]);

  const tooltipFormatter = useCallback(
    (params: any) => {
      const param = Array.isArray(params) ? params[0] : params;
      let items = Array.isArray(params) ? params : [params];
      if (tooltipFilter) {
        items = items.filter((item) => tooltipFilter(item, param));
      }

      let title = param.name;
      if (secondXDataKey && debouncedData[param.dataIndex][secondXDataKey]) {
        if (isTimeSlot) {
          title = `${debouncedData[param.dataIndex][secondXDataKey]} - ${
            param.name
          } (${getTimeRange(parseInt(param.name))})`;
        } else {
          title = `${debouncedData[param.dataIndex][secondXDataKey]} - ${
            param.name
          }`;
        }
      } else if (isTimeSlot) {
        title = getTimeRange(parseInt(param.name)) + ` (${param.name})`;
      }

      const sortedItems = [...items].sort((a, b) => {
        const nameA = (a.seriesName || "").toLowerCase();
        const nameB = (b.seriesName || "").toLowerCase();
        return nameA.localeCompare(nameB);
      });

      const now = new Date();
      const currentMonth = monthNames[now.getMonth()];
      const currentYear = now.getFullYear().toString();

      const tooltipItems = sortedItems.map((item) => {
        const year = item.seriesName?.trim();
        let displaySeriesName = item.seriesName;
        if (isPriceForecast) {
          if (item.name === currentMonth && year === currentYear) {
            displaySeriesName = `${currentYear} Forecast`;
          }
        }

        const value = parseFloat(item.value)
          .toFixed(2)
          .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        const bgColor = item.color;

        const legend = legends.find((l) => l.name === item.seriesName);

        let suffix = "";

        if (legend?.unit !== undefined) {
          suffix = legend.unit;
        } else {
          const suffixDict = {
            Price: COST_UNIT,
            Percent: "%",
            "%": "%",
            Cost: "Rs",
            Revenue: "Rs",
          };

          const foundKey = Object.keys(suffixDict).find((key) =>
            item.seriesName?.toString().includes(key)
          );

          suffix = foundKey
            ? suffixDict[foundKey as keyof typeof suffixDict]
            : legend?.unit || unit || "";
        }
        return `
    <div class="tooltip-item" data-name="${displaySeriesName?.toLowerCase()}">
      <div class="color-indicator" style="background-color: ${bgColor}"></div>
      <div class="series-name">${
        hidePrice ? displaySeriesName.replace("Price", "") : displaySeriesName
      }</div>
      <div class="value">${value} ${suffix}</div>
    </div>
  `;
      });

      const tooltipId = `tooltip-${Date.now()}`;

      return `
        <div class="enhanced-tooltip" id="${tooltipId}">
          <div class="tooltip-header">
            <div class="tooltip-title">${title}</div>
          </div>
          
          <div class="tooltip-content">
            ${tooltipItems.join("")}
          </div>
        </div>
        
        <style>
          .enhanced-tooltip {
            font-family: 'Inter', system-ui, sans-serif;
            width: 100%;
            min-width: 200px;
          }
          .tooltip-header {
            display: flex;
            flex-direction: column;
            gap: 8px;
            justify-content: start;
            align-items: start;
            border-bottom: 1px solid #eaeaea;
            padding-bottom: 8px;
            margin-bottom: 8px;
          }
          .tooltip-title {
            font-weight: 600;
            font-size: ${fontSize + 1}px;
            color: #333;
          }
          .tooltip-content {
            max-height: 300px;
            overflow-y: auto;
            padding-right: 10px;
          }
          .tooltip-item {
            display: flex;
            align-items: center;
            padding: 4px 0;
            border-bottom: 1px solid #f0f0f0;
          }
          .tooltip-item:last-child {
            border-bottom: none;
          }
          .color-indicator {
            width: 10px;
            height: 10px;
            border-radius: 50%;
            margin-right: 8px;
          }
          .series-name {
            flex: 1;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            font-size: ${fontSize}px;
          }
          .value {
            font-weight: 500;
            margin-left: 10px;
            font-size: ${fontSize}px;
          }
        </style>
      `;
    },
    [
      tooltipFilter,
      secondXDataKey,
      debouncedData,
      isTimeSlot,
      fontSize,
      isPriceForecast,
      legends,
      hidePrice,
      unit,
    ]
  );

  const chartOptions = useMemo(() => {
    if (!debouncedData || debouncedData.length === 0) return {};

    const series = orderedSelectedLegends
      .filter((legend) => !hiddenStats.includes(legend.name || ""))
      .map((legend, index) => {
        const isSelected =
          selectedLegends.some((item) => item.name === legend.name) ||
          selectedLegends.length === 0;

        const color =
          isSelected && colorMap[legend.name || ""]
            ? legend.stroke ?? colorMap[legend.name || ""]
            : getLegendColor(legend, index, selectedLegends);

        const seriesData = debouncedData.map((item) => item[legend.dataKey]);

        const commonOptions = {
          name: legend.name,
          data: seriesData,
          yAxisIndex: legend.yAxisId === "right" ? 1 : 0,
          itemStyle: { color },
        };

        if (legend.type === ChartType.Bar) {
          const customBarColor = "#f1935c";

          const barSeriesData = debouncedData.map((item, idx) => {
            const legendYear = (legend.dataKey || "").split("_")[0];
            if (isPriceForecast) {
              const now = new Date();
              const currentMonth = monthNames[now.getMonth()];
              const currentYear = now.getFullYear().toString();
              if (
                item.monthGroup === currentMonth &&
                legendYear === currentYear &&
                item[legend.dataKey] != null
              ) {
                return {
                  value: item[legend.dataKey],
                  itemStyle: { color: customBarColor },
                };
              }
            }
            return {
              value: item[legend.dataKey],
            };
          });

          return {
            ...commonOptions,
            type: "bar",
            stack: legend.stackId || null,
            barGap: barCategoryGap + "%",
            barMaxWidth: 80,
            data: barSeriesData,
            itemStyle: {
              ...commonOptions.itemStyle,
              borderRadius: BAR_RADIUS,
            },
          };
        } else if (legend.type === ChartType.Area) {
          return {
            ...commonOptions,
            type: "line",
            stack: legend.stackId || null,
            areaStyle: { color },
            symbol: "none",
            lineStyle: { width: 3 },
          };
        } else {
          const isSinglePoint = seriesData.length === 1;
          return {
            ...commonOptions,
            large: !isSinglePoint,
            largeThreshold: 10000,
            sampling: isSinglePoint ? undefined : "lttb",
            progressive: isSinglePoint ? undefined : 5000,
            showSymbol: isSinglePoint,
            symbolSize: isSinglePoint ? 6 : undefined,
            hoverAnimation: false,
            animation: false,
            type: "line",
            smooth: !isSinglePoint,
            symbol: isSinglePoint ? "circle" : "none",
            lineStyle: {
              width: 2,
              type: legend.isDash ? "dashed" : "solid", // Support for dashed lines
              // Optional: customize dash pattern
              // dashOffset: 0,
              // For dashed: [5, 5] = 5px dash, 5px gap
              // For dotted: [2, 2] or use type: "dotted"
            },
          };
        }
      });

    const brushStartIndex =
      debouncedData && brushStart === BrushStart.Start
        ? 0
        : debouncedData.length > 95
        ? debouncedData.length - 96
        : 0;

    const brushEndIndex =
      debouncedData && brushStart === BrushStart.Start
        ? 95
        : debouncedData.length > 96
        ? debouncedData.length - 1
        : 0;

    return {
      grid: {
        left: left ?? 40,
        right: 50,
        bottom: showBrush
          ? brushHeight + xaxisHeight - (isSmallLegend ? 30 : 10)
          : xaxisHeight - bottom,
        top: top || 30,
        containLabel: true,
      },

      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "line",
          snap: true,
          label: {
            show: false,
          },
          xAxisIndex: 0,
        },
        formatter: customTooltip ? customTooltip : tooltipFormatter,
        enterable: true,
        confine: true,
        appendToBody: true,
        backgroundColor: "#fff",
        borderRadius: 10,
        textStyle: {
          fontSize: fontSize,
          fontWeight: 500,
          color: "#333",
        },
        extraCssText: `
        box-shadow: 0px 0px 10px 0px rgba(0,0,0,0.1); 
        border: none;
        max-height: calc(100vh - 350px); 
        overflow-y: auto;
        padding-right: 10px;
      `,
      },

      toolbox: {
        show: showToolbox ?? true,
        top: -5,
        feature: {
          dataZoom: {
            yAxisIndex: "none",
          },
          restore: {},
          saveAsImage: {},
        },
      },

      xAxis: [
        {
          type: "category",
          data: xAxisData,
          axisLabel: {
            fontSize: fontSize,
            rotate: xAxisAngle || 0,
            margin: xAxisTickMargin + 4,
            color: "#8C8A96",
          },
          axisLine: { lineStyle: { color: "#E0E0E0" } },
          axisTick: {
            show: true,
            alignWithLabel: true,
            length: 4,
            lineStyle: {
              color: "#E0E0E0",
            },
          },
          name: xLabel,
          nameLocation:
            xAxisLabelPosition === "insideBottom"
              ? "middle"
              : xAxisLabelPosition,
          nameGap: xAxisLabelOffset || 15,
          nameTextStyle: {
            fontSize: labelFontSize ?? fontSize + 3,
            color: SecondaryColor,
          },
        },

        ...(secondXDataKey
          ? (() => {
              const secondXAxisData = debouncedData.map(
                (item) => item[secondXDataKey]
              );
              const uniqueDates = [...new Set(secondXAxisData)];
              const blocksPerDate = Math.floor(
                debouncedData.length / uniqueDates.length
              );
              const centerPosition = Math.floor(blocksPerDate / 2);

              const visibleStartIndex = brushIndex?.startIndex ?? 0;
              const visibleEndIndex =
                brushIndex?.endIndex ?? debouncedData.length - 1;
              const visibleDataLength = visibleEndIndex - visibleStartIndex + 1;
              const visibleDateCount = Math.ceil(
                visibleDataLength / blocksPerDate
              );

              const getDateInterval = (index: number) => {
                const dateGroupIndex = Math.floor(index / blocksPerDate);
                const isAtCenterOfDateGroup =
                  index % blocksPerDate === centerPosition;

                if (!isAtCenterOfDateGroup) return false;

                let skipFactor = 1;
                if (visibleDateCount > 20) skipFactor = 3;
                else if (visibleDateCount > 15) skipFactor = 2;
                else if (visibleDateCount > 7) skipFactor = 1;

                return dateGroupIndex % skipFactor === 0;
              };

              return [
                {
                  axisLine: { lineStyle: { color: "#E0E0E0" } },
                  type: "category",
                  axisPointer: { show: false },
                  position: "bottom",
                  nameLocation: "end",
                  nameTextStyle: {
                    fontSize: labelFontSize ?? fontSize + 3,
                    color: SecondaryColor,
                  },
                  axisLabel: {
                    color: "#8C8A96",
                    interval: getDateInterval,
                    fontSize: visibleDateCount > 15 ? fontSize - 1 : fontSize,
                  },
                  offset: 25,
                  data: secondXAxisData,
                },
              ];
            })()
          : []),
      ],

      yAxis: [
        {
          type: "value",
          ...(yMaxValue ? { max: yMaxValue } : {}),
          splitNumber: 5,

          axisLabel: {
            fontSize: fontSize,
            margin: 0, // Add 8px margin for spacing from axis line
            formatter: (value: number) => {
              // Format large numbers with K/M suffix
              if (value >= 1000000) {
                return (value / 1000000).toFixed(1) + "M";
              } else if (value >= 1000) {
                return (value / 1000).toFixed(1) + "K";
              }
              // Handle small decimal values (between 0 and 10)
              else if (value < 10 && value > 0) {
                // Show appropriate decimal places based on magnitude
                if (value < 0.01) {
                  return value.toFixed(4);
                } else if (value < 0.1) {
                  return value.toFixed(3);
                } else if (value < 1) {
                  return value.toFixed(2);
                } else {
                  return value.toFixed(1);
                }
              } else if (value === 0) {
                return "0";
              }
              return value;
            },
          },

          axisTick: {
            show: true,
            length: 4,
            lineStyle: {
              color: "#E0E0E0",
            },
          },

          scale: false,
          boundaryGap: [0, 0.1],

          name: yAxisLabel,
          nameLocation: "middle",
          nameGap: nameGap ?? 50,
          nameTextStyle: {
            fontSize: labelFontSize ?? fontSize,
            color: SecondaryColor,
            fontWeight: 600,
            fontFamily: "Inter, system-ui, sans-serif",
          },
        },
        {
          type: "value",
          position: "right",
          ...(secondYAxisDomain
            ? { min: secondYAxisDomain[0], max: secondYAxisDomain[1] }
            : {}),
          alignTicks: true,
          splitNumber: 5,

          axisLabel: {
            fontSize: fontSize,
            margin: 0, // Add 8px margin for spacing from axis line
            formatter: (value: number) => {
              // Format large numbers with K/M suffix
              if (value >= 1000000) {
                return (value / 1000000).toFixed(1) + "M";
              } else if (value >= 1000) {
                return (value / 1000).toFixed(1) + "K";
              }
              // Handle decimal values - round to 1 decimal place for cleaner display
              else if (value % 1 !== 0) {
                return value.toFixed(1);
              }
              // For values at or near zero, show as is
              else if (value === 0) {
                return "0";
              }
              // For whole numbers, show as integer
              return Math.round(value).toString();
            },
          },


          axisTick: {
            show: true,
            length: 4,
            lineStyle: {
              color: "#E0E0E0",
            },
          },

          scale: false,
          boundaryGap: [0, 0.1],

          name: secondYAxisLabel,
          nameLocation: "middle",
          nameGap: nameGap ?? 50,
          nameTextStyle: {
            fontSize: labelFontSize ?? fontSize,
            color: SecondaryColor,
            fontWeight: 600,
            fontFamily: "Inter, system-ui, sans-serif",
          },
        },
      ],

      series,

      dataZoom: [
        ...(showBrush
          ? [
              {
                type: "slider",
                start:
                  ((brushIndex?.startIndex ?? brushStartIndex) /
                    debouncedData.length) *
                  100,
                end:
                  ((brushIndex?.endIndex ?? brushEndIndex) /
                    debouncedData.length) *
                  100,
                height: brushHeight,
                width: "98%",
                bottom: 10,
                left: 10,
                brushSelect: false,
                xAxisIndex: [1, 0],
                textStyle: {
                  color: SecondaryColor,
                },
                handleIcon:
                  "M10.7,11.9v-1.3H9.3v1.3c-4.9,0.3-8.8,4.4-8.8,9.4c0,5,3.9,9.1,8.8,9.4v1.3h1.3v-1.3c4.9-0.3,8.8-4.4,8.8-9.4C19.5,16.3,15.6,12.2,10.7,11.9z M13.3,24.4H6.7V23h6.6V24.4z M13.3,19.6H6.7v-1.4h6.6V19.6z",
                handleSize: "80%",
              },
            ]
          : []),
        ...(enableMouseWheelZoom
          ? [
              {
                type: "inside",
                xAxisIndex: [1, 0],
                zoomLock: false,
                zoomOnMouseWheel: true,
                moveOnMouseMove: true,
                preventDefaultMouseMove: false,
              },
            ]
          : []),
      ],
    };
  }, [debouncedData, orderedSelectedLegends, brushStart, left, showBrush, brushHeight, xaxisHeight, isSmallLegend, bottom, top, customTooltip, tooltipFormatter, fontSize, showToolbox, xAxisData, xAxisAngle, xAxisTickMargin, xLabel, xAxisLabelPosition, xAxisLabelOffset, labelFontSize, secondXDataKey, yMaxValue, yAxisLabel, nameGap, secondYAxisDomain, secondYAxisLabel, brushIndex?.startIndex, brushIndex?.endIndex, enableMouseWheelZoom, hiddenStats, selectedLegends, colorMap, barCategoryGap, isPriceForecast]);

  const onChartEvents = {
    click: (params: any) => {
      if (onBarClick && params.seriesType === "bar") {
        onBarClick(params);
      }
    },
    finished: () => {
      requestAnimationFrame(() => dispatch(setLoadingFilterName("")));
    },
    legendselectchanged: (params: any) => {
      const selectedNames = Object.entries(params.selected)
        .filter(([name, isSelected]) => isSelected)
        .map(([name]) => name);

      const newSelectedLegends = legends.filter(
        (legend) =>
          legend.name !== undefined && selectedNames.includes(legend.name)
      );
      setSelectedLegends(newSelectedLegends);

      if (onLegendSelect) {
        onLegendSelect(newSelectedLegends);
      }
    },
  };

  return (
    <>
      {/* Chart container */}
      <div style={{ height: chartContainerHeight }} className="chart-container">
        <style>
          {`
            @media (max-width: 768px) {
              .chart-container {
                height: 350px !important;
              }
              .enhanced-tooltip {
                min-width: 180px !important;
              }
              .tooltip-title {
                font-size: ${fontSize - 1}px !important;
              }
              .series-name, .value {
                font-size: ${fontSize - 2}px !important;
              }
              .tooltip-content {
                max-height: 250px !important;
              }
            }
            @media (max-width: 480px) {
              .chart-container {
                height: 300px !important;
              }
              .enhanced-tooltip {
                min-width: 160px !important;
              }
              .tooltip-title {
                font-size: ${fontSize - 2}px !important;
              }
              .series-name, .value {
                font-size: ${fontSize - 3}px !important;
              }
              .tooltip-content {
                max-height: 200px !important;
              }
            }
          `}
        </style>
        <ReactECharts
          ref={chartRef}
          option={chartOptions}
          style={{ height: "100%", width: "100%" }}
          opts={{ renderer: "canvas" }}
          lazyUpdate={true}
          onEvents={onChartEvents}
        />
      </div>

      {/* Legend display area */}
      <div
        style={{ maxHeight: legendMaxHeight || "17%" }}
        className={`flex flex-wrap justify-center gap-x-2 overflow-legend ${
          showBrush && !isSmallLegend && "pt-3"
        }`}
      >
        <style>
          {`
            @media (max-width: 768px) {
              .overflow-legend {
                gap: 0.5rem !important;
                padding: 0.5rem !important;
              }
              .line-legend {
                font-size: ${
                  legendFontSize ? legendFontSize - 2 : fontSize - 2
                }px !important;
              }
            }
            @media (max-width: 480px) {
              .overflow-legend {
                gap: 0.25rem !important;
                padding: 0.25rem !important;
              }
              .line-legend {
                font-size: ${
                  legendFontSize ? legendFontSize - 3 : fontSize - 3
                }px !important;
              }
            }
          `}
        </style>
        {filteredLegends
          .slice()
          .sort((a, b) => {
            // Special handling: if both legends are month-like (e.g., "Apr-25", "JAN-25"),
            // sort them chronologically ascending.
            const monthAbbrs = [
              "JAN",
              "FEB",
              "MAR",
              "APR",
              "MAY",
              "JUN",
              "JUL",
              "AUG",
              "SEP",
              "OCT",
              "NOV",
              "DEC",
            ];

            const parseMonthKey = (name?: string): number | null => {
              if (!name) return null;
              const trimmed = name.trim();
              // Only consider the first 6 characters for detection (e.g., "Apr-25"),
              // anything after that is ignored for pattern matching
              const head6 = trimmed.slice(0, 6);
              const match = /^([A-Za-z]{3})-(\d{2})$/.exec(head6);
              if (!match) return null;
              const monthIdx = monthAbbrs.indexOf(match[1].toUpperCase());
              if (monthIdx < 0) return null;
              const yy = parseInt(match[2], 10);
              const year = 2000 + yy; // Interpret 2-digit year as 20yy
              return year * 12 + monthIdx; // Comparable numeric key
            };

            const aKey = parseMonthKey(a.name);
            const bKey = parseMonthKey(b.name);

            // If both are month-like, sort chronologically
            if (aKey !== null && bKey !== null) {
              return aKey - bKey;
            }

            // If only one is month-like, keep month-like before non-month-like for consistency
            if (aKey !== null && bKey === null) return -1;
            if (aKey === null && bKey !== null) return 1;

            // Fall back to custom order only if BOTH items exist in the order list
            if (customLegendOrder) {
              const ai = customLegendOrder.indexOf(a.name || "");
              const bi = customLegendOrder.indexOf(b.name || "");
              const aIn = ai >= 0;
              const bIn = bi >= 0;
              if (aIn && bIn) return ai - bi;
              if (aIn && !bIn) return -1;
              if (!aIn && bIn) return 1;
            }

            // Default: case-insensitive alphabetical as a stable tie-breaker
            return (a.name || "")
              .toLowerCase()
              .localeCompare((b.name || "").toLowerCase());
          })
          .map((legend, index) => {
            const isHidden = hiddenLegends.includes(legend.name ?? "");

            return (
              <React.Fragment key={index}>
                <div className={`mb-3 ${isHidden ? "hidden" : ""}`}>
                  {LegendComponent(
                    legend,
                    index,
                    selectedLegends,
                    handleLegendClick,
                    circleLegend,
                    legendFontSize,
                    colorMap, // Add this parameter
                    isSmallLegend
                  )}
                </div>

                {legendBreakIndex != 0 && legendBreakIndex === index && (
                  <div style={{ width: "100%", height: "2px" }}></div>
                )}
              </React.Fragment>
            );
          })}
      </div>
    </>
  );
}
